/**
 * Browser geolocation helper.
 *
 * UAT batch-A / batch-B (issue #4): the Enable Location prompt used to
 * repeat on every dashboard mount because we never remembered the
 * permission state or the last known position. This module now:
 *
 *  1. Consults the Permissions API up front so we can skip the browser
 *     prompt when the user already denied us.
 *  2. Caches the last successful coords in sessionStorage (10 minutes)
 *     so a full-page reload doesn't re-prompt.
 *  3. Rejects with a `code`-annotated Error so the UI can render the
 *     right message + fallback ("PERMISSION_DENIED",
 *     "POSITION_UNAVAILABLE", "TIMEOUT", "UNSUPPORTED").
 */

const CACHE_KEY = 'scensob:lastKnownLocation';
const CACHE_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') return null;
    if (!parsed.ts || Date.now() - parsed.ts > CACHE_MAX_AGE_MS) return null;
    return { lat: parsed.lat, lng: parsed.lng };
  } catch (_e) {
    return null;
  }
}

function writeCache(coords) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ lat: coords.lat, lng: coords.lng, ts: Date.now() }),
    );
  } catch (_e) {
    /* storage full or blocked */
  }
}

function clearCache() {
  try { sessionStorage.removeItem(CACHE_KEY); } catch (_e) { /* noop */ }
}

/**
 * Public helper — used by the "Disable Location" button. Wipes the
 * session cache so the app stops offering "nearby jobs" until the user
 * re-enables. We CAN'T revoke the browser permission itself (no JS API
 * for that in any modern browser), but at the app layer this is enough
 * to stop sending coordinates.
 */
export function disableLocation() { clearCache(); }

/**
 * Detect the user's browser so we can render the right "how to re-enable"
 * instructions. Returns one of: 'chrome' | 'edge' | 'firefox' | 'safari' | 'other'.
 */
export function detectBrowser() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'safari';
  return 'other';
}

/**
 * Returns "granted" | "denied" | "prompt" | "unknown".
 * Safe to call at any time — never throws.
 */
export async function getLocationPermissionState() {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state || 'unknown';
  } catch (_e) {
    return 'unknown';
  }
}

/**
 * Resolve current coords, honoring the browser permission state and a
 * short-lived session cache. `opts.forceFresh = true` bypasses the cache.
 * `opts.silent = true` returns null (never throws) if permission is denied
 * — useful for background refreshes.
 */
export async function getCurrentLocation(opts = {}) {
  const { forceFresh = false, silent = false } = opts;

  if (!forceFresh) {
    const cached = readCache();
    if (cached) return cached;
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    const err = new Error('Geolocation is not supported by this browser.');
    err.code = 'UNSUPPORTED';
    if (silent) return null;
    throw err;
  }

  // Skip prompting the user if they've already denied us — the browser
  // will silently fire an error otherwise and we'd toast "please enable"
  // over and over. BUT: honor `forceFresh` from an explicit user click on
  // "Enable Location" — we still can't force a re-prompt when denied, but
  // we should let the caller see the native PERMISSION_DENIED so it can
  // pop the "here's how to re-enable" instructions modal.
  const state = await getLocationPermissionState();
  if (state === 'denied' && !forceFresh) {
    const err = new Error('Location permission was denied.');
    err.code = 'PERMISSION_DENIED';
    if (silent) return null;
    throw err;
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        writeCache(coords);
        resolve(coords);
      },
      (error) => {
        // Normalize the browser's GeolocationPositionError → our own
        // machine-friendly error with a stable `code` string.
        let code = 'UNKNOWN';
        if (error) {
          if (error.code === 1) code = 'PERMISSION_DENIED';
          else if (error.code === 2) code = 'POSITION_UNAVAILABLE';
          else if (error.code === 3) code = 'TIMEOUT';
        }
        if (code === 'PERMISSION_DENIED') clearCache();
        if (silent) { resolve(null); return; }
        const wrapped = new Error(error?.message || 'Could not determine your location.');
        wrapped.code = code;
        wrapped.original = error;
        reject(wrapped);
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 5 * 60 * 1000, // browser cache: 5 min
      }
    );
  });
}

export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export function formatDistance(meters) {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}
